// App.jsx
import './App.css';
import React from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Toolbar,
} from '@mui/material';

import ButtonAppBar, { DRAWER_WIDTH } from './components/app/Appbar';
import { AuthProvider } from './components/auth/AuthContext';
import { useAuth } from './components/auth/AuthContext';

import LandingPage from "./components/app/LandingPage";
import Dashboard from "./components/app/Dashboard";
import Profile from "./components/profile/Profil";
import Account from "./components/profile/Account";
import Registration from './components/auth/Registration';
import RegistrationSuccess from './components/auth/RegistrationSuccess';
import Login from './components/auth/Login';
import LoginSuccess from './components/auth/LoginSuccess';
import PaymentThankYou from './components/billing/PaymentThankYou';
import ArticleManagement from "./components/billing/ArticleManagement";
import ShippingCostManagement from "./components/billing/ShippingCostManagement";
import CustomerManagement from "./components/billing/CustomerManagement";
import OrderManagement from "./components/billing/OrderManagement";
import InvoiceManagement from "./components/billing/InvoiceManagement";
import OfferManagement from "./components/billing/OfferManagement";
import RecurringInvoiceManagement from "./components/billing/RecurringInvoiceManagement";
import PaymentTransactions from "./components/billing/PaymentTransactions.jsx";
import { resolvePaymentsTabFromSearch } from "./components/billing/paymentTransactionsRouteUtils";
import Evaluation from "./components/reports/Evaluation";
import NotFound from "./components/app/NotFound";
import ErrorPage from "./components/app/ErrorPage";
import SupplierManagement from "./components/billing/SupplierManagement.jsx";
import OpenInvoiceOverview from "./components/billing/OpenInvoiceOverview";
import AssetManagement from "./components/assets/AssetManagement.jsx";
import AssetReport from "./components/assets/AssetReport.jsx";
import FinancialReport from "./components/reports/FinancialReport.jsx";
import CashflowForecast from "./components/reports/CashflowForecast.jsx";
import PayrollIntegration from "./components/admin/PayrollIntegration.jsx";
import RoleManagement from "./components/admin/RoleManagement.jsx";
import ApprovalWorkflow from './components/admin/ApprovalWorkflow.jsx';
import Privacy from "./components/legal/Privacy.jsx";
import Imprint from "./components/legal/Imprint.jsx";
import Terms from "./components/legal/Terms.jsx";
import Settings from "./components/settings/Settings.jsx";

import { setAuthErrorHandler } from "./apiClient";
import { logDebug, logInfo } from './logging/loggingService';
import { ChatbotWidget } from './chatbot';
import useTokenCountdown from './hooks/useTokenCountdown';
import useTokenRefreshOnActivity from './hooks/useTokenRefreshOnActivity';
import DevOps from "./components/admin/DevOps.jsx";
import { ensureFreshToken, isKeycloakMode } from './auth/keycloakClient';
import { getStoredAccessToken, getTokenExpiration } from './utils/tokenStorage';
import {
    getAuthEscalationSuppressionReason,
    getAuthEscalationSuppressionRemainingMs,
    isAuthEscalationSuppressed,
} from './auth/authEscalationGuard';

/**
 * Dieses Shell-Component lebt *innerhalb* des Routers,
 * hat damit Zugang zu useNavigate und registriert den globalen
 * Auth-Fehler-Handler (401/403 -> Redirect + Token Clear).
 */
function AppShell() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, logout } = useAuth();
    const pathnameRef = React.useRef(location.pathname);
    const suppressedExpiryRetryRef = React.useRef(null);
    const [sessionExpiredOpen, setSessionExpiredOpen] = React.useState(false);

    pathnameRef.current = location.pathname;

    const handleTokenExpired = React.useCallback(async () => {
        if (isAuthEscalationSuppressed()) {
            const retryInMs = Math.max(100, getAuthEscalationSuppressionRemainingMs() + 100);
            if (suppressedExpiryRetryRef.current) {
                window.clearTimeout(suppressedExpiryRetryRef.current);
            }
            suppressedExpiryRetryRef.current = window.setTimeout(() => {
                suppressedExpiryRetryRef.current = null;
                handleTokenExpired();
            }, retryInMs);
            logInfo('Token expiry escalation suppressed', {
                component: 'AppShell',
                pathname: pathnameRef.current,
                suppressionReason: getAuthEscalationSuppressionReason(),
                retryInMs,
            });
            return;
        }
        if (suppressedExpiryRetryRef.current) {
            window.clearTimeout(suppressedExpiryRetryRef.current);
            suppressedExpiryRetryRef.current = null;
        }
        if (isKeycloakMode()) {
            try {
                const refreshedToken = await ensureFreshToken(30);
                if (refreshedToken) {
                    return;
                }
            } catch {}
            // Keep Keycloak SSO session intact; just clear local auth state.
            logout({ keycloakLogout: false });
        } else {
            const refreshedAccessToken = getStoredAccessToken();
            if (refreshedAccessToken) {
                const refreshedExpiration = getTokenExpiration(refreshedAccessToken);
                if (typeof refreshedExpiration === 'number' && refreshedExpiration > Date.now()) {
                    logInfo('Skipping delayed expiry logout because access token is fresh again', {
                        component: 'AppShell',
                        pathname: pathnameRef.current,
                    });
                    return;
                }
            }
            logout();
        }
        if (pathnameRef.current !== '/login') {
            setSessionExpiredOpen(true);
        }
    }, [logout]);

    const handleSessionExpiredConfirm = React.useCallback(() => {
        setSessionExpiredOpen(false);
        if (pathnameRef.current !== '/login') {
            // Use a reason flag so /login can decide whether to auto-redirect to Keycloak.
            navigate('/login?reason=expired', { replace: true });
        }
    }, [navigate]);

    // Trigger local logout when token countdown reaches zero in both auth modes.
    useTokenCountdown({ onExpire: handleTokenExpired, enabled: isAuthenticated });
    useTokenRefreshOnActivity({ enabled: isAuthenticated });

    React.useEffect(() => {
        logDebug('Registering global auth error handler', { component: 'AppShell' });
        const handler = (status) => {
            if (status === 401) {
                handleTokenExpired();
            } else if (status === 403) {
                navigate("/login?reason=forbidden", { replace: true });
            }
        };

        setAuthErrorHandler(handler);
        return () => {
            logDebug('Removing global auth error handler', { component: 'AppShell' });
            setAuthErrorHandler(null); // Cleanup beim Unmount
        };
    }, [handleTokenExpired, navigate]);

    React.useEffect(() => {
        logInfo('Navigation change detected', {
            component: 'AppShell',
            pathname: location.pathname,
            search: location.search,
        });
    }, [location]);

    React.useEffect(() => {
        return () => {
            if (suppressedExpiryRetryRef.current) {
                window.clearTimeout(suppressedExpiryRetryRef.current);
                suppressedExpiryRetryRef.current = null;
            }
        };
    }, []);

    return (
        <>
            <ButtonAppBar />
            <Box
                sx={{
                    display: 'flex',
                    minHeight: '100vh',
                    backgroundColor: 'background.default',
                }}
            >
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        width: '100%',
                        ml: {md: isAuthenticated ? `${DRAWER_WIDTH}px` : 0},
                        px: {xs: 2, md: 4},
                        py: {xs: 2, md: 4},
                    }}
                >
                    <Toolbar />
                    <Routes>
                        <Route path='/' element={<LandingPage />} />
                        <Route path='/dashboard' element={<Dashboard />} />
                        <Route path='/profile' element={<Profile />} />
                    <Route path='/account' element={<Account />} />
                    <Route path='/settings' element={<Settings />} />
                    <Route path='/registration' element={<Registration />} />
                    <Route path='/registrationSuccess' element={<RegistrationSuccess />} />
                    <Route path='/login' element={<Login />} />
                    <Route path='/loginSuccess' element={<LoginSuccess />} />
                    <Route path='/danke' element={<PaymentThankYou />} />
                    <Route path='/article' element={<ArticleManagement />} />
                    <Route path='/shipping-costs' element={<ShippingCostManagement />} />
                    <Route path='/customer' element={<CustomerManagement />} />
                    <Route path='/orders' element={<OrderManagement />} />
                    <Route path='/offers' element={<OfferManagement />} />
                    <Route path='/invoices' element={<InvoiceManagement />} />
                    <Route path='/open-invoices' element={<OpenInvoiceOverview />} />
                    <Route path='/recurring-invoices' element={<RecurringInvoiceManagement />} />
                    <Route path='/payments' element={<PaymentsRoute />} />
                    <Route path='/incomes' element={<PaymentsRoute defaultTab="incomes" />} />
                    <Route path='/suppliers' element={<SupplierManagement />} />
                    <Route path='/expenses' element={<PaymentsRoute defaultTab="expenses" />} />
                    <Route path='/paypal-import' element={<PaymentsRoute defaultTab="unassigned" />} />
                    <Route path='/payroll' element={<PayrollIntegration />} />
                    <Route path='/assets' element={<AssetManagement />} />
                    <Route path='/asset-report' element={<AssetReport />} />
                    <Route path='/financial-report' element={<FinancialReport />} />
                    <Route path='/cashflow' element={<CashflowForecast />} />
                    <Route path='/evaluation' element={<Evaluation />} />
                    <Route path='/approvals' element={<ApprovalWorkflow />} />
                    <Route path='/devops' element={<DevOps />} />
                    <Route path='/admin/roles' element={<RoleManagement />} />
                    <Route path='/privacy' element={<Privacy />} />
                    <Route path='/imprint' element={<Imprint />} />
                    <Route path='/terms' element={<Terms />} />
                    <Route path='/error' element={<ErrorPage />} />
                        <Route path='*' element={<NotFound />} />
                    </Routes>
                </Box>
            </Box>
            <Dialog
                open={sessionExpiredOpen}
                onClose={handleSessionExpiredConfirm}
                aria-labelledby="session-expired-title"
                aria-describedby="session-expired-description"
            >
                <DialogTitle id="session-expired-title">Sitzung abgelaufen</DialogTitle>
                <DialogContent>
                    <DialogContentText id="session-expired-description">
                        Du wurdest ausgeloggt. Bitte erneut einloggen.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSessionExpiredConfirm} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {isAuthenticated && <ChatbotWidget />}
        </>
    );
}

function PaymentsRoute({ defaultTab = "incomes" }) {
    const location = useLocation();
    const initialTab = resolvePaymentsTabFromSearch(location.search, defaultTab);
    return <PaymentTransactions initialTab={initialTab} />;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppShell />
            </BrowserRouter>
        </AuthProvider>
    );
}
